import { Icon } from '@chakra-ui/icon'
import { QuestionIcon as IconUnknown } from '@chakra-ui/icons'
import { Box, Flex, HStack, Text } from '@chakra-ui/layout'
import React, { FC, forwardRef } from 'react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconCopy from '~/assets/svgs/icon-copy.svg?react'
import IconEmptyFolder from '~/assets/svgs/icon-empty-folder.svg?react'
import IconFax from '~/assets/svgs/icon-fax.svg?react'
import IconIntegrationCurve from '~/assets/svgs/icon-integration-curve.svg?react'
import IconIntegrationDenticon from '~/assets/svgs/icon-integration-denticon.svg?react'
import IconIntegrationDentrix from '~/assets/svgs/icon-integration-dentrix.svg?react'
import IconIntegrationEagleSoft from '~/assets/svgs/icon-integration-eaglesoft.svg?react'
import IconIntegrationMango from '~/assets/svgs/icon-integration-mango.svg?react'
import IconIntegrationOpenDental from '~/assets/svgs/icon-integration-opendental.svg?react'
import IconMessaging from '~/assets/svgs/icon-messaging.svg?react'
import {
  BlockedNumber,
  useBlockedNumbers,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { UnblockNumber } from '~/components/compositional/QuickActions'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { HighlightText } from '~/components/shared/HighlightText'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { ContactsOperation } from '~/hooks/toast/eventHandlers/contactsToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { ContactSourceType } from '@mangovoice/types'

import { useContactItemStyle } from './componentStyles/ContactItem.style'
import { useContactItemsStyle } from './componentStyles/ContactItems.style'
import { useContactSourceStyle } from './componentStyles/ContactSource.style'
import {
  ContactActions,
  ContactDetails,
  ContactNumber,
} from './viewModels/Contacts.viewmodel'

const pmsMap: Record<
  ContactSourceType,
  { pmsName: string; Icon: FC<React.SVGProps<SVGSVGElement>> }
> = {
  [ContactSourceType.Curve]: {
    pmsName: 'Curve',
    Icon: IconIntegrationCurve,
  },
  [ContactSourceType.Denticon]: {
    pmsName: 'Denticon',
    Icon: IconIntegrationDenticon,
  },
  [ContactSourceType.Dentrix]: {
    pmsName: 'Dentrix',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixAscend]: {
    pmsName: 'Dentrix Ascend',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixG7]: {
    pmsName: 'Dentrix G7',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixEnterprise]: {
    pmsName: 'Dentrix Enterprise',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.Eaglesoft]: {
    pmsName: 'EagleSoft',
    Icon: IconIntegrationEagleSoft,
  },
  [ContactSourceType.OpenDental]: {
    pmsName: 'Open Dental',
    Icon: IconIntegrationOpenDental,
  },
  [ContactSourceType.Extensions]: {
    pmsName: 'Mango',
    Icon: IconIntegrationMango,
  },
  [ContactSourceType.Pbxes]: { pmsName: 'Mango', Icon: IconIntegrationMango },
  [ContactSourceType.Unknown]: {
    pmsName: 'Unknown',
    Icon: IconUnknown as React.FunctionComponent<
      React.SVGProps<SVGSVGElement> & {
        title?: string | undefined
      }
    >,
  },
}

const tryGetPmsRecordOrDefault = (pms: ContactSourceType) => {
  if (pms in pmsMap) {
    return pmsMap[pms]
  }

  return pmsMap[ContactSourceType.Unknown]
}
interface ContactSourceProps {
  sourceName: string
  pms: ContactSourceType
}
const ContactSource = ({ sourceName, pms }: ContactSourceProps) => {
  const { pmsName, Icon: pmsIcon } = tryGetPmsRecordOrDefault(pms)

  const styles = useContactSourceStyle()
  return (
    // Source Container
    <Flex sx={styles.container}>
      {/* Source Label */}
      <Text sx={styles.labelText}>Contact Source:</Text>
      {/* Source Data Container */}
      <Flex sx={styles.dataContainer}>
        {/* Source Icon Container */}
        <Flex sx={styles.iconContainer}>
          {/* Source Icon */}
          <Icon sx={styles.icon} as={pmsIcon} />
        </Flex>
        {/* Source Details Container */}
        <Flex direction="column">
          {/* Source Name */}
          <Text sx={styles.nameText}>{sourceName}</Text>
          {/* Source PMS */}
          <Text sx={styles.pmsText}>From {pmsName}</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

interface ActionButtonProps {
  label: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  onClick: () => void
}
const ActionButton = forwardRef(function ActionButton(
  { label, icon, onClick: handleClick }: ActionButtonProps,
  ref: React.Ref<HTMLButtonElement> | undefined,
) {
  return (
    <QuickActionButton
      ref={ref}
      variant={QuickActionButtonVariants.Default_100}
      size={Size.sm}
      actionIcon={icon}
      tooltipText={label}
      onClick={handleClick}
    />
  )
})

const getActionButton = (
  index: number,
  action: ContactActions,
  onClick: () => void,
) => {
  switch (action) {
    case 'call':
      return (
        <ActionButton
          key={index}
          label="Call Number"
          icon={IconCallStart}
          onClick={onClick}
        />
      )
    case 'message':
      return (
        <ActionButton
          key={index}
          label="Message Number"
          icon={IconMessaging}
          onClick={onClick}
        />
      )
    case 'fax':
      return (
        <ActionButton
          key={index}
          label="Send Fax"
          icon={IconFax}
          onClick={onClick}
        />
      )
  }
}

interface ContactItemProps {
  key?: string | number
  contactDetails: ContactDetails
  contactNumber: ContactNumber
  searchTerm?: string
  blockedNumbers?: BlockedNumber[]
  onCallClick?: (callee: {
    contactNumber: ContactNumber
    contact: ContactDetails
  }) => void
  onMessagingClick?: (number: string) => void
  onFaxClick?: (number: string) => void
  onCopyClick?: (number: string) => void
  onUnblockClick?: (number: BlockedNumber) => void
}
const ContactItem = ({
  contactNumber,
  contactDetails,
  searchTerm = '',
  blockedNumbers,
  onCallClick,
  onMessagingClick,
  onFaxClick,
  onCopyClick,
  onUnblockClick,
}: ContactItemProps) => {
  const { emit } = useEmitterService()

  const handleCallStartClick = () => {
    onCallClick?.({ contactNumber, contact: contactDetails })
  }
  const handleMessagingClick = () => {
    onMessagingClick?.(contactNumber.number)
  }
  const handleFaxClick = () => {
    onFaxClick?.(contactNumber.number)
  }
  const handleCopyClick = () => {
    onCopyClick?.(formatPhoneNumber(contactNumber.number))
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }
  const actionHandlerMap: Record<ContactActions, () => void> = {
    call: handleCallStartClick,
    message: handleMessagingClick,
    fax: handleFaxClick,
  }

  const blockedNumber = useFindBlockedNumber(
    blockedNumbers,
    contactNumber.number,
  )
  const handleUnblockClick = () => {
    if (!blockedNumber) {
      return
    }
    onUnblockClick?.(blockedNumber)
  }

  console.log(`hasBlockedNumber: ${blockedNumber} in ${blockedNumbers?.length}`)

  const styles = useContactItemStyle()
  return (
    <Flex sx={styles.container}>
      <Flex sx={styles.dataContainer}>
        {!contactNumber.isExtension && (
          <Flex sx={styles.labelContainer}>
            <Text sx={styles.labelText}>
              {toNounCasing(contactNumber.label)}
            </Text>
            {contactNumber.isPrimary && (
              <Flex sx={styles.primaryBadge}>Primary</Flex>
            )}
          </Flex>
        )}
        {contactNumber.isExtension && (
          <ExtensionBadge
            size={Size.xl}
            extension={contactNumber.number}
            searchTerm={searchTerm}
          />
        )}
        {!contactNumber.isExtension && (
          <HighlightText
            sx={styles.numberText}
            query={[searchTerm]}
            permutationFilters={['-', ' ', '(', ')']}
          >
            {formatPhoneNumber(contactNumber.number)}
          </HighlightText>
        )}{' '}
      </Flex>
      {/* Action Stack */}
      <HStack gap="0.25rem">
        {blockedNumber && (
          <UnblockNumber
            size={Size.sm}
            variant={QuickActionButtonVariants.Destructive_100}
            onClick={handleUnblockClick}
          />
        )}
        {contactNumber.availableActions.map((action, index) =>
          getActionButton(index, action, actionHandlerMap[action]),
        )}
        <ActionButton
          label="Copy Number"
          icon={IconCopy}
          onClick={handleCopyClick}
        />
      </HStack>
    </Flex>
  )
}
const EmptyContactItem = () => {
  const styles = useContactItemStyle()
  return (
    <Flex sx={styles.empty_container}>
      <Icon as={IconEmptyFolder} sx={styles.empty_icon} />
      <Text sx={styles.empty_text}>
        No phone numbers saved for this contact yet.
      </Text>
    </Flex>
  )
}

interface ContactItemsProps {
  contactDetails: ContactDetails
  searchTerm?: string
  onCallClick: (callee: {
    contactNumber: ContactNumber
    contact: ContactDetails
  }) => void
  onMessagingClick: (number: string) => void
  onFaxClick: (number: string) => void
  onCopyClick: (number: string) => void
}
export const ContactItems = ({
  contactDetails,
  searchTerm,
  onCallClick,
  onMessagingClick,
  onFaxClick,
  onCopyClick,
}: ContactItemsProps) => {
  const styles = useContactItemsStyle()

  const { blockedNumbers, unblockNumber } = useBlockedNumbers()

  const handleUnblockNumber = (number: BlockedNumber) => {
    unblockNumber(number.id, number.phoneNumber)
  }

  return (
    <Flex sx={styles.container}>
      <Text sx={styles.text}>Contact Information</Text>
      <Box sx={styles.scrollContainer} className="scrollbar-hidden">
        {contactDetails?.numbers?.map((contactNumber, index) => {
          return (
            <ContactItem
              key={index}
              contactNumber={contactNumber}
              contactDetails={contactDetails}
              blockedNumbers={blockedNumbers}
              searchTerm={searchTerm}
              onCallClick={onCallClick}
              onMessagingClick={onMessagingClick}
              onFaxClick={onFaxClick}
              onCopyClick={onCopyClick}
              onUnblockClick={handleUnblockNumber}
            />
          )
        }) ?? <EmptyContactItem />}
        <ContactSource
          sourceName={contactDetails?.sourceName}
          pms={contactDetails?.source}
        />
      </Box>
    </Flex>
  )
}
